<template>
    <button
        :id="`select-option-${uuid}`"
        :aria-disabled="disabled ? 'true' : 'false'"
        :class="[
            'relative block flex items-center w-full text-left select-none relative py-2 border-gray-200 scroll-mt-10',
            'transition duration-150',
            'focus:outline-none',
            state.multiple ? 'px-3' : 'pl-3 pr-9',
            isSingleSelected ? 'font-semibold text-blue-500' : '',
            disabled
                ? 'text-gray-400 cursor-not-allowed italic'
                : 'hover:bg-gray-100 focus:bg-gray-150',
            divided ? 'border-t border-gray-200' : '',
        ]"
        :data-option="true"
        :data-selected="isSelected ? 'true' : null"
        data-testid="select-option"
        :data-value="value"
        type="button"
        @click="onSelect"
        @keydown.enter.prevent.stop="onSelect"
        @keydown.space.prevent.stop="onSelect"
    >
        <activix-checkbox
            class="mr-3"
            data-testid="select-option-checkbox"
            :disabled="disabled"
            :size="checkboxSize"
            :model-value="isSelected"
            @update:model-value="onSelect"
            v-if="state.multiple && showCheckbox"
        />
        <div class="inline-flex mr-2" data-testid="select-option-icon" v-if="$slots.icon">
            <slot name="icon" />
        </div>
        <div
            class="flex items-center w-4 mr-2 shrink-0"
            :class="iconStyle"
            data-testid="select-option-prop-icon"
            v-else-if="icon"
        >
            <span class="flex items-center" v-html="icon" v-if="isInlineSvgIcon" />
            <icon :class="iconStyle" :name="icon" v-else-if="isNamedIcon" />
            <i :class="icon" v-else-if="isCssClassIcon" />
        </div>
        <slot :is-selected="isSelected">
            <span
                class="block truncate"
                :class="[colorClassName, { 'font-bold': bold }]"
                data-testid="select-option-label"
                :title="label"
                v-text="label"
            />
        </slot>
        <span class="w-4 ml-2 shrink-0" data-testid="select-option-suffix-icon" v-if="suffixIcon">
            <span v-html="suffixIcon" v-if="suffixIcon.includes('svg')" />
        </span>
        <span
            class="ml-2 flex items-center justify-center"
            data-testid="select-option-tooltip"
            v-tooltip="tooltip"
            v-if="tooltip"
        >
            <information-circle class="h-4 w-4 text-gray-500" />
        </span>
        <span
            class="absolute inset-y-0 right-0 flex items-center justify-center w-9 pointer-events-none"
            data-testid="select-option-checkmark"
            v-if="isSingleSelected && showCheckmark"
        >
            <interface-validation-check
                :class="[
                    colorClassName || 'text-blue-500',
                    oldFontScale ? 'h-3.5 w-3.5' : 'h-3 w-3',
                ]"
            />
        </span>
    </button>
</template>

<script lang="ts" setup>
    import { computed, inject, getCurrentInstance, toRef } from 'vue';
    import { v4 as uuidv4 } from 'uuid';
    import InterfaceValidationCheck from '@/components/activix-ui/ActivixSelect/assets/check.svg';
    import InformationCircle from '@/components/activix-ui/ActivixSelect/assets/information-circle.svg';
    import ActivixCheckbox from '@/components/activix-ui/ActivixCheckbox/ActivixCheckbox.vue';
    // Explicit import needed: the prop named "icon" shadows the global <icon> component
    // in <script setup> binding resolution, causing Vue to use the prop value as a tag name.
    import Icon from '@/components/elements/ActivixIcon.vue';
    import type {
        SelectState,
        SelectEvents,
        SelectOptionValue,
    } from '@/components/activix-ui/ActivixSelect/types.ts';
    import { CheckboxSize } from '@/components/activix-ui/ActivixCheckbox/types.ts';

    defineOptions({ compatConfig: { MODE: 3 } });

    const props = withDefaults(
        defineProps<{
            label?: string;
            value?: SelectOptionValue;
            disabled?: boolean;
            tooltip?: string;
            showCheckmark?: boolean;
            showCheckbox?: boolean;
            colorClassName?: string;
            icon?: string;
            iconStyle?: string;
            suffixIcon?: string;
            bold?: boolean;
            divided?: boolean;
        }>(),
        {
            label: '',
            disabled: false,
            tooltip: '',
            showCheckmark: true,
            showCheckbox: true,
            colorClassName: '',
            icon: '',
            iconStyle: '',
            suffixIcon: '',
            bold: false,
            divided: false,
        }
    );

    const instance = getCurrentInstance();
    const state = inject<SelectState>('state')!;
    const events = inject<SelectEvents>('events')!;
    const oldFontScale = inject<boolean>('oldFontScale', true);

    const uuid = uuidv4();

    const isSelected = computed(() => {
        return state.selectedOptions.some((selectedOption) => {
            return selectedOption.value === props.value;
        });
    });

    const isSingleSelected = computed(() => {
        return isSelected.value && !state.multiple;
    });

    const checkboxSize = computed(() => {
        return CheckboxSize.SM;
    });

    /** Icon contains raw SVG markup (e.g., '<svg>...</svg>') - render directly with v-html */
    const isInlineSvgIcon = computed(() => props.icon.includes('svg'));

    /** Icon is a named icon path (e.g., 'regular/arrow-down') - use the Icon component */
    const isNamedIcon = computed(() => props.icon.includes('/'));

    /** Icon is a CSS class (e.g., 'fa fa-check') - render with <i> element */
    const isCssClassIcon = computed(
        () => props.icon && !isInlineSvgIcon.value && !isNamedIcon.value
    );

    const onSelect = (event?: Event | KeyboardEvent) => {
        if (props.disabled) {
            return;
        }

        const isEnter =
            event instanceof KeyboardEvent && (event.code === 'Enter' || event.key === 'Enter');

        events.onSelect(props.value ?? null, instance?.proxy?.$el as HTMLElement, isEnter);
    };

    defineExpose({
        disabled: toRef(props, 'disabled'),
        isSelected,
        isFocused: computed(() => document.activeElement === instance?.proxy?.$el),
        value: toRef(props, 'value'),
        onSelect,
    });
</script>
